import React, { useState, useCallback } from 'react';

import { GLOBAL_TIME_PENALTY_DEFAULTS } from './TimePenaltyChart';
import { GLOBAL_TEE_PENALTY_DEFAULTS } from './TeePenaltyTable';

const BAN_PENALTY = 20;
const FRIEND_BONUS = -3.0;

const calculateTimePenalty = (timeTable, targetHours, targetMinutes) => {
  const table = timeTable.slice().sort((a, b) => (a.hours * 60 + a.minutes) - (b.hours * 60 + b.minutes));
  const targetMins = targetHours * 60 + targetMinutes;

  for (let i = 0; i < table.length - 1; i++) {
    const t1 = table[i].hours * 60 + table[i].minutes;
    const v1 = table[i].value;
    const t2 = table[i + 1].hours * 60 + table[i + 1].minutes;
    const v2 = table[i + 1].value;

    if (targetMins === t1) return v1;
    if (targetMins === t2) return v2;
    if (t1 < targetMins && targetMins < t2) {
      return v1 + (v2 - v1) * ((targetMins - t1) / (t2 - t1));
    }
  }

  const firstMin = table[0].hours * 60 + table[0].minutes;
  if (targetMins <= firstMin) return table[0].value;
  if (targetMins >= table[table.length - 1].hours * 60 + table[table.length - 1].minutes) {
    return table[table.length - 1].value;
  }
  return 0;
};

const getTeeScore = (teeTable, tee) => {
  const match = teeTable.find(t => t.tee === tee);
  if (!match || match.score === undefined) return 5;
  return match.score;
};

const calculateFreeSpotsPenalty = (spotsFree, nrInGroup) => {
  const penForLess = 10;
  if (spotsFree >= nrInGroup) {
    const penForMore = (4 - nrInGroup) * 0.012;
    return (spotsFree - nrInGroup) * penForMore;
  }
  return (nrInGroup - spotsFree) * penForLess;
};

const calculatePlayerPenalty = (friendsBooked, blockedBooked) => {
  return friendsBooked * FRIEND_BONUS + blockedBooked * BAN_PENALTY;
};

const timeSlots = [];
for (let h = 6; h <= 14; h++) {
  for (let m = (h === 6 ? 30 : 0); m <= 59; m += 10) {
    if (h === 14 && m > 30) break;
    timeSlots.push({ hours: h, minutes: m });
  }
}

const sortedTees = [...GLOBAL_TEE_PENALTY_DEFAULTS].sort((a, b) => a.tee - b.tee);

function lerpColor(minVal, maxVal, val) {
  if (maxVal === minVal) {
    return '#c8e6c9';
  }
  const t = (val - minVal) / (maxVal - minVal);
  // light green (#c8e6c9) to light red (#ffcdd2)
  const r = Math.round(200 + t * (255 - 200));
  const g = Math.round(230 + t * (205 - 230));
  const b = Math.round(201 + t * (210 - 201));
  return 'rgb(' + r + ', ' + g + ', ' + b + ')';
}

function PenaltyHeatmap({ timePenaltyTable, teePenaltyTable }) {
  const [nrInGroup, setNrInGroup] = useState(4);
  const [spotsFree, setSpotsFree] = useState(4);
  const [friendsBooked, setFriendsBooked] = useState(0);
  const [blockedBooked, setBlockedBooked] = useState(0);
  const [heatmap, setHeatmap] = useState(null);

  const handleCalculate = useCallback(() => {
    const timeTable = timePenaltyTable || GLOBAL_TIME_PENALTY_DEFAULTS;
    const teeTable = teePenaltyTable || [];
    const mergedTeeTable = [];
    GLOBAL_TEE_PENALTY_DEFAULTS.forEach(def => {
      const override = teeTable.find(t => t.tee === def.tee);
      mergedTeeTable.push(override || def);
    });
    mergedTeeTable.push(...teeTable.filter(t => !GLOBAL_TEE_PENALTY_DEFAULTS.some(d => d.tee === t.tee)));

    const results = [];
    let minPenalty = Infinity;
    let maxPenalty = -Infinity;

    const playerPenalty = calculatePlayerPenalty(friendsBooked, blockedBooked);

    for (const slot of timeSlots) {
      const row = { hours: slot.hours, minutes: slot.minutes };
      for (const teeEntry of sortedTees) {
        const timePenalty = calculateTimePenalty(timeTable, slot.hours, slot.minutes);
        const teeScore = getTeeScore(mergedTeeTable, teeEntry.tee);
        const freeSpotsPenalty = calculateFreeSpotsPenalty(spotsFree, nrInGroup);
        const total = timePenalty + teeScore + freeSpotsPenalty + playerPenalty;

        row[teeEntry.tee] = total;
        if (total < minPenalty) minPenalty = total;
        if (total > maxPenalty) maxPenalty = total;
      }
      results.push(row);
    }

    setHeatmap({ rows: results, minPenalty, maxPenalty });
  }, [timePenaltyTable, teePenaltyTable, nrInGroup, spotsFree, friendsBooked, blockedBooked]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const handleNrInGroup = (e) => {
    const val = clamp(parseInt(e.target.value, 10), 1, 4);
    setNrInGroup(val);
  };

  const handleSpotsFree = (e) => {
    const val = clamp(parseInt(e.target.value, 10), 0, 4);
    setSpotsFree(val);
  };

  const handleFriendsBooked = (e) => {
    const val = clamp(parseInt(e.target.value, 10), 0, 3);
    setFriendsBooked(val);
  };

  const handleBlockedBooked = (e) => {
    const val = clamp(parseInt(e.target.value, 10), 0, 3);
    setBlockedBooked(val);
  };

  const formatTime = (h, m) => {
    const str = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    const ampm = h >= 12 ? 'pm' : 'am';
    return str + ' ' + ampm;
  };

  return (
    <div className="penalty-heatmap">
      <div className="heatmap-controls">
        <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
          <div>
            <label className="form-label small mb-0">Group Size</label>
            <input
              type="number"
              min={1}
              max={4}
              value={nrInGroup}
              onChange={handleNrInGroup}
              className="form-control form-control-sm"
              style={{ width: 70 }}
            />
          </div>
          <div>
            <label className="form-label small mb-0">Free Spots</label>
            <input
              type="number"
              min={0}
              max={4}
              value={spotsFree}
              onChange={handleSpotsFree}
              className="form-control form-control-sm"
              style={{ width: 70 }}
            />
          </div>
          <div>
            <label className="form-label small mb-0">Friends Booked <span className="text-muted">({FRIEND_BONUS}/ea)</span></label>
            <input
              type="number"
              min={0}
              max={3}
              value={friendsBooked}
              onChange={handleFriendsBooked}
              className="form-control form-control-sm"
              style={{ width: 70 }}
            />
          </div>
          <div>
            <label className="form-label small mb-0">Blocked Booked <span className="text-muted">({BAN_PENALTY}/ea)</span></label>
            <input
              type="number"
              min={0}
              max={3}
              value={blockedBooked}
              onChange={handleBlockedBooked}
              className="form-control form-control-sm"
              style={{ width: 70 }}
            />
          </div>
          <button type="button" className="btn btn-sm btn-primary" onClick={handleCalculate}>
            Calculate
          </button>
        </div>
      </div>

      {heatmap && (
        <div className="table-responsive mt-3">
          <table className="table table-sm heatmap-table" style={{ fontSize: '0.7rem' }}>
            <thead>
              <tr>
                <th className="text-nowrap sticky-left">Time</th>
                {sortedTees.map(tee => (
                  <th key={tee.tee} className="text-center">Tee {tee.tee}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmap.rows.map((row, rowIdx) => {
                const label = formatTime(row.hours, row.minutes);
                const isEvenHour = row.minutes === 0;
                return (
                  <tr key={rowIdx}>
                    <td className="text-nowrap fw-bold sticky-left" style={{ backgroundColor: '#fff', color: isEvenHour ? '#000' : '#666' }}>
                      {label}
                    </td>
                    {sortedTees.map(tee => {
                      const val = row[tee.tee];
                      const bgColor = lerpColor(heatmap.minPenalty, heatmap.maxPenalty, val);
                      const isBest = Math.abs(val - heatmap.minPenalty) < 0.001;
                      return (
                        <td
                          key={tee.tee}
                          className="text-center heatmap-cell"
                          style={{ backgroundColor: bgColor, color: '#222', border: isBest ? '2px solid #00c853' : undefined }}
                          title={`Tee ${tee.tee} at ${label}: ${val.toFixed(2)}`}
                        >
                          {val.toFixed(2)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-2 d-flex align-items-center gap-2">
            <span className="small text-muted">Best (lowest):</span>
            <span className="small fw-bold" style={{ color: '#2e7d32' }}>{heatmap.minPenalty.toFixed(2)}</span>
            <div className="flex-grow-1" />
            <span className="small text-muted">Worst (highest):</span>
            <span className="small fw-bold" style={{ color: '#c62828' }}>{heatmap.maxPenalty.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export { PenaltyHeatmap };
